import { Box, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import type { EmbeddedApplicationStatus } from '@/types/embeddedApplication';

interface EmbeddedStatusBarProps {
  status: EmbeddedApplicationStatus;
}

function resolveStatusTone(
  theme: Theme,
  variant: NonNullable<EmbeddedApplicationStatus['variant']>,
): string {
  if (variant === 'success') {
    return theme.palette.success.main;
  }

  if (variant === 'warning') {
    return theme.palette.warning.main;
  }

  if (variant === 'error') {
    return theme.palette.error.main;
  }

  return theme.palette.info.main;
}

export function EmbeddedStatusBar({ status }: EmbeddedStatusBarProps) {
  if (status.visible === false) {
    return null;
  }

  const variant = status.variant ?? 'success';

  return (
    <Box
      sx={{
        position: 'absolute',
        right: 16,
        bottom: 16,
        zIndex: 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        bgcolor: (theme) => {
          const tone = resolveStatusTone(theme, variant);
          return alpha(tone, theme.palette.mode === 'dark' ? 0.24 : 0.12);
        },
        border: '1px solid',
        borderColor: (theme) => {
          const tone = resolveStatusTone(theme, variant);
          return alpha(tone, theme.palette.mode === 'dark' ? 0.48 : 0.3);
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: (theme) => resolveStatusTone(theme, variant),
        }}
      />
      <Typography
        variant="chip"
        sx={{
          color: (theme) => resolveStatusTone(theme, variant),
          textTransform: 'uppercase',
        }}
      >
        {status.label}
      </Typography>
    </Box>
  );
}
